import { useTranslation } from 'react-i18next'

import Icon from '../../../../ui/Icon'
import { DropdownMenu } from '../../../../dropdownMenu/DropdownMenu'
import { CourtSvg } from '../../../../ui/courtSvg/CourtSvg'
import { CourtNightLighting } from './courtNightLighting/CourtNightLighting'

import { CourtType, CourtModelMetadata } from '../../../../../types/court/court'
import { ActionType } from '../../../../../constants/actionTypes'

type CourtCardProps = {
  court: CourtModelMetadata
  onAction: (court: CourtModelMetadata, action: ActionType) => void
}

export const CourtCard = ({
  court,
  onAction,
}: CourtCardProps) => {
  const { t } = useTranslation('courts')

  // Times arrive as HH:mm or HH:mm:ss; the card only needs hours and minutes.
  const openingHours = court.openTime && court.closeTime
    ? `${court.openTime.slice(0, 5)} – ${court.closeTime.slice(0, 5)}`
    : '-'

  const isIndoor = court.type === CourtType.INDOOR

  return (
    <div className="ph-card court-card">
      <div
        className={`court-card-preview d-none d-md-flex ${
          isIndoor ? 'court-card-preview-indoor' : 'court-card-preview-outdoor'
        }`}
      >
        <CourtSvg indoor={isIndoor} w={90} h={160} />
      </div>

      <div className="court-card-content">
        <div className="ph-row ph-between court-card-header">
          <div className="court-card-title">
            <h3>
              {court.name}
              <span className="court-card-id mono muted">#{court.id}</span>
            </h3>
          </div>
          <div className="ph-row court-card-corner">
            <span
              className={`badge ${isIndoor ? 'badge-info' : 'badge-success'}`}
            >
              <Icon
                name={isIndoor ? CourtType.INDOOR : CourtType.OUTDOOR}
                size={11}
              />
              <span>{t(`courts.type.${court.type}`)}</span>
            </span>
            <DropdownMenu
              label={t('courts.card.more-actions')}
              items={[
                {
                  key: 'edit',
                  label: t('courts.card.edit'),
                  icon: 'edit',
                  onClick: () => onAction(court, ActionType.EDIT),
                },
                {
                  key: 'block',
                  label: t('courts.card.block'),
                  icon: 'block',
                  onClick: () => onAction(court, ActionType.BLOCK),
                },
                {
                  key: 'delete',
                  label: t('courts.card.delete'),
                  icon: 'trash',
                  danger: true,
                  onClick: () => onAction(court, ActionType.DELETE),
                },
              ]}
            />
          </div>
        </div>

        <div className="court-card-details">
          <div className="ph-row ph-between court-card-detail">
            <span className="muted">{t('courts.card.configuration')}</span>
            <span className="mono">{court.clubName || '-'}</span>
          </div>
          <div className="ph-row ph-between court-card-detail">
            <span className="muted">{t('courts.card.city')}</span>
            <span className="mono">{court.clubAddress?.city || '-'}</span>
          </div>
          <div className="ph-row ph-between court-card-detail">
            <span className="muted">{t('courts.card.opening-hours')}</span>
            <span className="mono">{openingHours}</span>
          </div>
        </div>
      </div>

      {court.lightSwitchName && (
        <CourtNightLighting lightSwitchName={court.lightSwitchName} />
      )}
    </div>
  )
}
